<template>
  <footer class="border-t border-white/10 bg-hilab text-white">
    <div class="mx-auto w-full max-w-none px-4 sm:px-8 lg:px-12 xl:px-16 py-12">
      <div class="grid gap-10" :class="gridColsClass">
        <!-- Company Info Column -->
        <div class="space-y-4">
          <a :href="brandUrl" class="flex items-center gap-3 text-lg font-semibold text-white">
            <img
              :src="logoSrc"
              :alt="companyInfo.name || brandName"
              class="h-10 w-10 rounded-full border border-white/20 object-cover bg-white/10"
            />
            {{ companyInfo.name || brandName }}
          </a>
          
          <p v-if="companyInfo.description" class="text-sm leading-relaxed text-white/80">
            {{ companyInfo.description }}
          </p>
          
          <!-- Contact Information -->
          <div class="space-y-3">
            <div v-if="companyInfo.address" class="flex items-start gap-3 text-sm text-white/70">
              <LucideIcon name="map-pin" :size="18" class="mt-0.5 flex-shrink-0" />
              <span class="leading-relaxed">{{ companyInfo.address }}</span>
            </div>
            
            <div v-if="companyInfo.email" class="flex items-center gap-3 text-sm text-white/90">
              <lucide-icon name="mail" :size="18" class="flex-shrink-0" />
              <a :href="`mailto:${companyInfo.email}`" class="hover:text-white transition-colors">
                {{ companyInfo.email }}
              </a>
            </div>
            
            <div v-if="companyInfo.phone" class="flex items-center gap-3 text-sm text-white/90">
              <lucide-icon name="phone-call" :size="18" class="flex-shrink-0" />
              <a :href="`tel:${companyInfo.phone}`" class="hover:text-white transition-colors">
                {{ companyInfo.phone }}
              </a>
            </div>
          </div>
        </div>

        <!-- Dynamic Columns -->
        <div v-for="(column, index) in columns" :key="index">
          <h4 class="text-sm font-semibold uppercase text-white/60">{{ column.title }}</h4>
          <ul class="mt-4 space-y-3 text-sm text-white/80">
            <li v-for="link in column.links" :key="link.text">
              <router-link :to="link.url" class="hover:text-white">{{ link.text }}</router-link>
            </li>
          </ul>
        </div>
      </div>

      <!-- Social Media & Copyright Row -->
      <div class="mt-12 flex flex-col gap-4 border-t border-white/10 pt-6 text-xs text-white/70 md:flex-row md:items-center md:justify-between">
        <!-- Social Media (Left) -->
        <div v-if="socialMedia.length > 0" class="flex items-center gap-4">
          <a 
            v-for="social in socialMedia" 
            :key="social.platform" 
            :href="social.url" 
            :title="getSocialDisplayName(social.platform)"
            class="hover:text-white transition p-2 rounded-full hover:bg-white/10"
            target="_blank"
            rel="noopener noreferrer"
          >
            <SocialIcon :platform="social.platform" :size="20" />
          </a>
        </div>
        <div v-else class="flex items-center gap-4">
          <!-- Debug: Show if no social media data -->
          <span class="text-white/50 text-xs">No social media configured</span>
        </div>

        <!-- Copyright (Right) -->
        <div class="flex flex-wrap items-center gap-4">
          <p>{{ copyrightText }}</p>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { computed } from 'vue';
import SocialIcon from './SocialIcon.vue';
import LucideIcon from '../../dashboard/components/LucideIcon.vue';
import defaultLogo from '../assets/logo_thumbnail.svg';

const props = defineProps({
  brandName: {
    type: String,
    required: true,
  },
  brandUrl: {
    type: String,
    required: true,
  },
  logoUrl: {
    type: String,
    default: '',
  },
  supportEmail: {
    type: String,
    required: true,
  },
  content: {
    type: Object,
    default: () => ({}),
  },
});

const logoSrc = computed(() => props.logoUrl || defaultLogo);

// Footer content from API
const footerContent = computed(() => props.content || {});

// Company information
const companyInfo = computed(() => footerContent.value.company_info || {});

// Dynamic columns (4 columns max)
const columns = computed(() => {
  const cols = footerContent.value.columns || [];
  return cols.slice(0, 4); // Limit to 4 columns max
});

// Grid columns class based on number of columns (max 5: 1 company + 4 columns)
const gridColsClass = computed(() => {
  const colCount = 1 + columns.value.length; // 1 for company info + dynamic columns
  if (colCount <= 1) return 'md:grid-cols-1';
  if (colCount <= 2) return 'md:grid-cols-2';
  if (colCount <= 3) return 'md:grid-cols-3';
  if (colCount <= 4) return 'md:grid-cols-4';
  return 'md:grid-cols-5'; // Max 5 columns (1 company + 4 custom)
});

// Social media links
const socialMedia = computed(() => footerContent.value.social_media || []);

// Copyright text (fixed)
const copyrightText = computed(() => {
  return `© ${new Date().getFullYear()} ${props.brandName || 'HiCHAT'}. Toàn bộ quyền được bảo lưu.`;
});


// Get social media display name
const getSocialDisplayName = (platform) => {
  const nameMap = {
    facebook: 'Facebook',
    twitter: 'Twitter',
    linkedin: 'LinkedIn',
    instagram: 'Instagram',
    youtube: 'YouTube',
    tiktok: 'TikTok',
    zalo: 'Zalo'
  };
  return nameMap[platform] || platform;
};
</script>
